<template lang="pug">
.study-answer-horizontal
  //- タイトル部
  .study-answer-horizontal__title
    .study-answer-horizontal__title__text.font-bold.text-large
      .study-answer-horizontal__title__text__problem-number
        .study-answer-horizontal__title__text__problem-number__label
          slot(name='problemNumberBeforeLabel')
        .study-answer-horizontal__title__text__problem-number__value {{ problemNumber }}
        .study-answer-horizontal__title__text__problem-number__label
          slot(name='problemNumberAfterLabel')
      .study-answer-horizontal__title__text__question-number {{ questionNumberText }}
    //- 「後で見直す」チェックボックス
    .study-answer-horizontal__title__check
      input.study-answer-horizontal__title__check__input(
        type='checkbox',
        :value='questionId',
        v-model='checkedQuestions',
        :id='`check_question_${questionId}`'
      )
      label-base.study-answer-horizontal__title__check__label(:for='`check_question_${questionId}`')
        slot(name='questionCheckboxLabel')

  //- 解答入力部
  .study-answer-horizontal__content(v-if='!isChangingProblem')
    //- 指示文
    .study-answer-horizontal__content__question-text(v-html='indicativeText', v-if='indicativeText')

    //- テキスト、テキストエリア、プルダウン、手書き/キーボード
    template(v-for='(data, index) in questionDatas', v-if='questionDatas')
      component.study-answer-horizontal__content__input(
        :is='inputComponentType',
        v-model='data.inputValue',
        :beforeText='data.beforeText',
        :afterText='data.afterText',
        :items='data.selectItems',
        :inputType='data.inputType',
        :lang='data.lang',
        :savedStrokes='data.savedStrokes',
        :answerFormat='answerFormat',
        :mathKeyboardName='mathKeyboardName',
        @set-input-type='setInputType($event, index)',
        @set-stroke-data='setStrokeData($event, index)'
      )
        template(#tegakiResultTitle)
          slot(name='tegakiResultTitle')
        template(#tegakiRecognizeButtonLabel)
          slot(name='tegakiRecognizeButtonLabel')

    //- 単一選択、複数選択、並び替え
    template(v-if='questionChoiceData')
      component.study-answer-horizontal__content__choice(
        :is='choiceComponentType',
        v-model='questionChoiceData.inputValue',
        :items='questionChoiceData.choiceItems',
        :labelNotDisplayable='questionChoiceData.labelNotDisplayable',
        :canDeselected='questionChoiceData.canDeselectedOrder'
      )

  //- 次へボタン
  button-next.study-answer-horizontal__button(v-if='showNextButton', @click='nextEvent')
    slot(name='nextButtonLabel')
  button-primary.study-answer-horizontal__button(v-else, @click='completeEvent')
    slot(name='completeButtonLabel')
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator'
import InputTextFieldHorizontal from '@/components/organisms/student/InputTextFieldHorizontal.vue'
import InputTextareaHorizontal from '@/components/organisms/student/InputTextareaHorizontal.vue'
import InputSingleChoice from '@/components/organisms/student/InputSingleChoice.vue'
import InputMultiChoice from '@/components/organisms/student/InputMultiChoice.vue'
import InputDialogHorizontal from '@/components/organisms/student/InputDialogHorizontal.vue'
import InputPulldown from '@/components/organisms/student/InputPulldown.vue'
import InputChangeOrder from '@/components/organisms/student/InputChangeOrder.vue'
import LabelBase from '@/components/atoms/LabelBase.vue'
import ButtonNext from '@/components/molecules/student/ButtonNext.vue'
import ButtonPrimary from '@/components/molecules/student/ButtonPrimary.vue'
import { StudyQuestionData, StudyQuestionChoiceData, InputDialogType } from '@/models/studyModel'
import { StrokePoint } from '@/models/StrokePoint'
import { AnswerFormats, AnswerFormat } from '@/const/AnswerFormats'

/**
 * 学習画面解答入力エリア（横書き）
 */
@Component({
  components: {
    InputTextFieldHorizontal,
    InputTextareaHorizontal,
    InputSingleChoice,
    InputMultiChoice,
    InputDialogHorizontal,
    InputPulldown,
    InputChangeOrder,
    LabelBase,
    ButtonNext,
    ButtonPrimary,
  },
})
export default class StudyAnswerHorizontal extends Vue {
  // 問題切替中フラグ
  @Prop()
  isChangingProblem!: boolean

  // 問題番号
  @Prop()
  problemNumber!: number

  // 設問番号テキスト
  @Prop()
  questionNumberText!: string

  // 問題ID
  @Prop()
  questionId!: number

  // チェックをした問題ID
  @Prop()
  checkedQuestionIds!: number[]

  // 指示文
  @Prop()
  indicativeText?: string

  // 入力形式
  @Prop()
  answerFormat!: AnswerFormat

  // 問題情報（テキスト、テキストエリア、プルダウン、手書き/キーボード）
  @Prop()
  questionDatas?: StudyQuestionData[]

  // 問題情報（単一選択、複数選択、並び替え）
  @Prop()
  questionChoiceData?: StudyQuestionChoiceData

  // 数学キーボード名
  @Prop()
  mathKeyboardName?: string

  // 次へボタン表示フラグ
  @Prop()
  showNextButton!: boolean

  // 次へボタン押下時イベント
  @Prop()
  nextEvent!: Function

  // 完了ボタン押下時イベント
  @Prop()
  completeEvent!: Function

  // 問題チェックのゲッター・セッター
  get checkedQuestions(): number[] {
    return this.checkedQuestionIds
  }
  set checkedQuestions(value: number[]) {
    this.$emit('check-question', value)
  }

  // 入力系コンポーネント名取得
  private get inputComponentType(): string {
    // 手書き/ソフトウェアキーボード用
    if (!!this.answerFormat.isTegaki || !!this.answerFormat.isKeyboard) {
      return 'InputDialogHorizontal'
    }

    // それ以外は入力フォーマットに応じたコンポーネントを返す
    switch (this.answerFormat) {
      case AnswerFormats.TEXT:
        return 'InputTextFieldHorizontal'
      case AnswerFormats.TEXTAREA:
        return 'InputTextareaHorizontal'
      case AnswerFormats.MULTI_PULLDOWN:
        return 'InputPulldown'
    }
    return ''
  }

  // 選択系コンポーネント名取得
  private get choiceComponentType(): string {
    switch (this.answerFormat) {
      case AnswerFormats.SINGLE_CHOICE:
        return 'InputSingleChoice'
      case AnswerFormats.MULTI_CHOICE:
        return 'InputMultiChoice'
      case AnswerFormats.SORT_CHOICE:
        return 'InputChangeOrder'
    }
    return ''
  }

  // 入力方法が手書き/キーボードかをセット
  private setInputType(inputType: InputDialogType, index: number) {
    if (this.questionDatas) {
      this.questionDatas[index].inputType = inputType
    }
  }

  // 手書きのストローク情報をセット
  private setStrokeData(strokes: StrokePoint[][], index: number) {
    if (this.questionDatas) {
      this.questionDatas[index].savedStrokes = strokes
    }
  }
}
</script>

<style lang="stylus" scoped>
.study-answer-horizontal
  position relative
  display flex
  flex-direction column
  border 2px solid var(--color-study-content__border)
  background-color var(--color-study-content__background)
  border-radius 4px
  padding 10px 20px 50px 20px

  &__title
    position relative
    display flex
    align-items center
    justify-content space-between
    color var(--color-study-content-title__text)
    background-color var(--color-study-content-title__background)
    padding 5px 20px
    margin-bottom 10px
    border-radius 10px
    &__text
      display flex
      &__problem-number
        display flex

    &__check
      position absolute
      right 30px
      &__label
        cursor pointer

  &__content
    overflow-y scroll

    &__question-text
      margin-bottom 10px

    &__input
      margin-bottom 10px
      display flex
      align-items center
      flex-direction row
      flex-wrap wrap
      justify-content flex-start

  &__button
    position absolute
    bottom 10px
    right 40px
    width 180px
</style>
